import { useCallback, useRef, useState } from 'react';

import { FileReceiver } from '~hooks/useComposerAttachments/useComposerAttachments';
import { Nullable } from '~types/Nullable';

const isDraggingFiles = (event: React.DragEvent) =>
  Array.from(event.dataTransfer.types).includes('Files');

// React bubbles drag events out of portals, so a dialog opened from inside the target would
// otherwise attach files dropped on it.
const isOverTarget = (event: React.DragEvent) =>
  event.target instanceof Node && event.currentTarget.contains(event.target);

export default function useFileDropTarget(receiver: Nullable<FileReceiver>) {
  const dragDepthRef = useRef(0);
  const [hasFilesOver, setHasFilesOver] = useState(false);

  const onDragEnter = useCallback((event: React.DragEvent) => {
    if (!isDraggingFiles(event)) return;

    event.preventDefault();

    if (!isOverTarget(event)) return;

    dragDepthRef.current += 1;
    setHasFilesOver(true);
  }, []);

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!isDraggingFiles(event)) return;

      event.preventDefault();
      event.dataTransfer.dropEffect = receiver && isOverTarget(event) ? 'copy' : 'none';
    },
    [receiver],
  );

  const onDragLeave = useCallback((event: React.DragEvent) => {
    if (!isDraggingFiles(event) || !isOverTarget(event)) return;

    dragDepthRef.current -= 1;

    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setHasFilesOver(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!isDraggingFiles(event)) return;

      event.preventDefault();
      dragDepthRef.current = 0;
      setHasFilesOver(false);

      const droppedFiles = Array.from(event.dataTransfer.files);

      if (receiver && isOverTarget(event) && droppedFiles.length > 0) {
        receiver(droppedFiles);
      }
    },
    [receiver],
  );

  return {
    dropTargetProps: { onDragEnter, onDragLeave, onDragOver, onDrop },
    isDraggingOver: Boolean(receiver) && hasFilesOver,
  };
}
